import React from "react";
import { FaWhatsapp } from "react-icons/fa";
import { Pill, Check, Sparkles, ShieldCheck } from "lucide-react";
import { useLang } from "../lib/LangContext";
import { waLink } from "../lib/mock";

export const Generics = () => {
  const { t } = useLang();
  const g = t.generics;

  return (
    <section className="px-6 md:px-12 lg:px-20 max-w-[1400px] mx-auto pt-10 pb-20 md:pt-14 md:pb-28">
      {/* Head */}
      <div className="max-w-3xl">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-lokam-tangerine">
          {g.eyebrow}
        </p>
        <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-lokam-forest text-balance">
          {g.title}
        </h2>
        <p className="mt-5 text-base md:text-lg text-lokam-ink-soft leading-relaxed">
          {g.subtitle}
        </p>
      </div>

      {/* Comparison */}
      <div className="mt-12 grid grid-cols-[1fr_auto_1fr] gap-2 sm:gap-4 lg:gap-8 items-stretch">
        {/* Prescribed brand */}
        <div
          data-testid="generics-brand-card"
          className="relative rounded-2xl md:rounded-3xl bg-white border border-lokam-border p-4 sm:p-6 md:p-8 flex flex-col"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-lokam-sand text-lokam-ink px-2.5 py-1 md:px-3 md:py-1.5 text-[9px] md:text-[11px] uppercase tracking-[0.14em] md:tracking-[0.18em] font-bold w-fit">
            {g.brand.kicker}
          </div>

          <div className="mt-4 md:mt-6 flex items-start gap-2 md:gap-4">
            <div className="h-9 w-9 md:h-12 md:w-12 rounded-lg md:rounded-xl bg-lokam-forest/5 text-lokam-forest flex items-center justify-center flex-shrink-0">
              <Pill className="h-4 w-4 md:h-6 md:w-6" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-lg sm:text-2xl md:text-3xl text-lokam-forest leading-tight break-words">
                {g.brand.name}
              </div>
              <div className="text-[11px] md:text-sm text-lokam-ink-soft mt-0.5 md:mt-1">
                by {g.brand.maker}
              </div>
            </div>
          </div>

          <div className="mt-5 md:mt-7 flex items-baseline gap-1.5 md:gap-2 flex-wrap">
            <div className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-lokam-ink tracking-tight">
              {g.brand.price}
            </div>
            <div className="text-[11px] md:text-sm text-lokam-ink-soft">
              {g.brand.unit}
            </div>
          </div>

          <div className="mt-4 md:mt-6 flex items-start gap-2 text-xs md:text-sm text-lokam-ink-soft">
            <Check className="h-3.5 w-3.5 md:h-4 md:w-4 text-lokam-forest flex-shrink-0 mt-0.5" />
            <span>{g.brand.bullet}</span>
          </div>

          <div className="mt-auto pt-6 md:pt-8">
            <div className="inline-flex items-center gap-2 text-xs md:text-sm font-semibold text-lokam-forest">
              {g.brand.action}
            </div>
          </div>
        </div>

        {/* Divider — vertical always */}
        <div className="flex flex-col items-center justify-center gap-2 md:gap-3 px-0.5 sm:px-1">
          <div className="w-px flex-1 bg-gradient-to-b from-transparent via-lokam-tangerine/40 to-transparent" />
          <div className="rounded-full border border-lokam-tangerine/30 bg-lokam-cream text-lokam-tangerine px-1.5 py-2 md:px-3 md:py-1.5 text-[9px] md:text-[11px] uppercase tracking-[0.12em] md:tracking-[0.18em] font-bold [writing-mode:vertical-rl] rotate-180 md:[writing-mode:horizontal-tb] md:rotate-0 whitespace-nowrap">
            {g.divider}
          </div>
          <div className="w-px flex-1 bg-gradient-to-b from-transparent via-lokam-tangerine/40 to-transparent" />
        </div>

        {/* Branded generic */}
        <div
          data-testid="generics-generic-card"
          className="relative rounded-2xl md:rounded-3xl bg-lokam-forest text-lokam-cream p-4 sm:p-6 md:p-8 flex flex-col overflow-hidden"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, #FFFDF9 1px, transparent 0)",
              backgroundSize: "26px 26px",
            }}
          />
          {/* Savings ribbon */}
          <div className="absolute top-3 right-3 md:top-5 md:right-5 z-10">
            <div className="rounded-full bg-lokam-tangerine text-white px-2 py-1 md:px-3 md:py-1.5 text-[9px] md:text-[11px] uppercase tracking-[0.14em] md:tracking-[0.18em] font-black shadow-lg">
              {g.generic.savings}
            </div>
          </div>

          <div className="relative inline-flex items-center gap-1.5 md:gap-2 rounded-full bg-lokam-cream/10 border border-white/15 text-lokam-tangerine px-2.5 py-1 md:px-3 md:py-1.5 text-[9px] md:text-[11px] uppercase tracking-[0.14em] md:tracking-[0.18em] font-bold w-fit max-w-[70%]">
            <Sparkles className="h-2.5 w-2.5 md:h-3 md:w-3 flex-shrink-0" />
            <span className="truncate">{g.generic.kicker}</span>
          </div>

          <div className="relative mt-4 md:mt-6 flex items-start gap-2 md:gap-4">
            <div className="h-9 w-9 md:h-12 md:w-12 rounded-lg md:rounded-xl bg-lokam-tangerine/15 text-lokam-tangerine flex items-center justify-center flex-shrink-0">
              <Pill className="h-4 w-4 md:h-6 md:w-6" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-lg sm:text-2xl md:text-3xl text-lokam-cream leading-tight break-words">
                {g.generic.name}
              </div>
              <div className="text-[11px] md:text-sm text-lokam-cream/70 mt-0.5 md:mt-1">
                {g.generic.maker}
              </div>
            </div>
          </div>

          <div className="relative mt-5 md:mt-7 flex items-baseline gap-1.5 md:gap-2 flex-wrap">
            <div className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-lokam-tangerine tracking-tight">
              {g.generic.price}
            </div>
            <div className="text-[11px] md:text-sm text-lokam-cream/70">
              {g.generic.unit}
            </div>
            <div className="ml-1 md:ml-2 line-through text-[11px] md:text-sm text-lokam-cream/40">
              {g.brand.price}
            </div>
          </div>

          <ul className="relative mt-4 md:mt-6 space-y-1.5 md:space-y-2.5">
            {g.generic.bullets.map((b, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-xs md:text-sm text-lokam-cream/85"
              >
                <Check className="h-3.5 w-3.5 md:h-4 md:w-4 text-lokam-tangerine flex-shrink-0 mt-0.5" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="relative mt-auto pt-6 md:pt-8">
            <div className="inline-flex items-center gap-2 text-xs md:text-sm font-semibold text-lokam-tangerine">
              {g.generic.action}
            </div>
          </div>
        </div>
      </div>

      {/* Reassurance chips */}
      <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2.5">
        {g.chips.map((chip, i) => (
          <span
            key={i}
            data-testid={`generics-chip-${i}`}
            className="inline-flex items-center gap-2 rounded-full border border-lokam-border bg-white px-3.5 py-2 text-xs md:text-sm font-medium text-lokam-ink"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-lokam-forest" />
            {chip}
          </span>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-8">
        <a
          href={waLink()}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="generics-cta"
          className="inline-flex items-center gap-2 rounded-full bg-lokam-whatsapp hover:bg-lokam-whatsapp-dark text-white px-5 py-3.5 text-sm md:text-base font-semibold transition-colors"
        >
          <FaWhatsapp className="h-5 w-5" />
          {g.cta}
        </a>
      </div>
    </section>
  );
};

export default Generics;
